/**
 * Inventory Firestore Service
 * Direct Firestore SDK calls for inventory expenses CRUD operations
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// Helper to convert Firestore timestamp to Date
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Helper to convert document to expense object
const docToExpense = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    date: convertTimestamp(data.date),
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt)
  };
};

/**
 * Get all inventory expenses with filters
 */
export const getAll = async (filters = {}) => {
  try {
    const inventoryRef = collection(db, 'inventory');
    let constraints = [];

    // Apply date filters
    if (filters.startDate && filters.endDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(filters.startDate))));
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    } else if (filters.startDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(filters.startDate))));
    } else if (filters.endDate) {
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    }

    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }

    if (filters.productId) {
      constraints.push(where('productId', '==', filters.productId));
    }

    if (filters.expenseType) {
      constraints.push(where('expenseType', '==', filters.expenseType));
    }

    if (filters.createdBy) {
      constraints.push(where('createdBy', '==', filters.createdBy));
    }

    // Order by date descending
    constraints.push(orderBy('date', 'desc'));

    // Apply limit
    if (filters.limit) {
      constraints.push(firestoreLimit(parseInt(filters.limit)));
    }

    const q = query(inventoryRef, ...constraints);
    const snapshot = await getDocs(q);

    const expenses = snapshot.docs.map(docToExpense);

    // Apply offset manually (Firestore doesn't have native offset)
    if (filters.offset) {
      return expenses.slice(parseInt(filters.offset));
    }

    return expenses;
  } catch (error) {
    console.error('Error fetching inventory:', error);
    throw error;
  }
};

/**
 * Get expense by ID
 */
export const getById = async (id) => {
  try {
    const expenseRef = doc(db, 'inventory', id);
    const expenseDoc = await getDoc(expenseRef);

    if (!expenseDoc.exists()) {
      throw new Error('Inventory expense not found');
    }

    return docToExpense(expenseDoc);
  } catch (error) {
    console.error('Error fetching expense:', error);
    throw error;
  }
};

/**
 * Create a new inventory expense
 */
export const create = async (expenseData) => {
  try {
    const quantity = parseFloat(expenseData.quantity) || 1;
    const amount = parseFloat(expenseData.amount) || 0;
    const unitCost = quantity > 0 ? amount / quantity : amount;

    const expenseDoc = {
      expenseType: expenseData.expenseType?.trim() || '',
      description: expenseData.description?.trim() || '',
      amount: amount,
      quantity: quantity,
      unitCost: unitCost,
      productId: expenseData.productId || null,
      category: expenseData.category?.trim() || null,
      date: expenseData.date ? Timestamp.fromDate(new Date(expenseData.date)) : Timestamp.now(),
      createdBy: expenseData.createdBy || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const inventoryRef = collection(db, 'inventory');
    const docRef = await addDoc(inventoryRef, expenseDoc);

    return {
      id: docRef.id,
      ...expenseDoc,
      date: expenseData.date ? new Date(expenseData.date) : new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

/**
 * Update an inventory expense
 */
export const update = async (id, updateData) => {
  try {
    const expenseRef = doc(db, 'inventory', id);

    const quantity = parseFloat(updateData.quantity) || 1;
    const amount = parseFloat(updateData.amount) || 0;
    const unitCost = quantity > 0 ? amount / quantity : amount;

    const updateDocData = {
      expenseType: updateData.expenseType?.trim() || '',
      description: updateData.description?.trim() || '',
      amount: amount,
      quantity: quantity,
      unitCost: unitCost,
      productId: updateData.productId || null,
      category: updateData.category?.trim() || null,
      date: updateData.date ? Timestamp.fromDate(new Date(updateData.date)) : Timestamp.now(),
      updatedAt: serverTimestamp()
    };

    await updateDoc(expenseRef, updateDocData);

    return await getById(id);
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

/**
 * Delete an inventory expense
 */
export const remove = async (id) => {
  try {
    const expenseRef = doc(db, 'inventory', id);
    await deleteDoc(expenseRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

/**
 * Get inventory statistics
 */
export const getStats = async (filters = {}) => {
  try {
    const inventoryRef = collection(db, 'inventory');
    let constraints = [];

    // Apply date filters
    if (filters.startDate && filters.endDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(filters.startDate))));
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    } else if (filters.startDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(filters.startDate))));
    } else if (filters.endDate) {
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    }

    const q = constraints.length > 0 ? query(inventoryRef, ...constraints) : query(inventoryRef);
    const snapshot = await getDocs(q);

    let totalAmount = 0;
    let totalQuantity = 0;
    let expenseCount = 0;
    const byCategory = {};
    const byProduct = {};
    const byType = {};

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const amount = parseFloat(data.amount) || 0;
      const quantity = parseFloat(data.quantity) || 0;

      totalAmount += amount;
      totalQuantity += quantity;
      expenseCount += 1;

      // Group by category
      const category = data.category || 'Uncategorized';
      if (!byCategory[category]) {
        byCategory[category] = { total: 0, count: 0 };
      }
      byCategory[category].total += amount;
      byCategory[category].count += 1;

      // Group by product if linked
      if (data.productId) {
        if (!byProduct[data.productId]) {
          byProduct[data.productId] = { total: 0, count: 0, productId: data.productId };
        }
        byProduct[data.productId].total += amount;
        byProduct[data.productId].count += 1;
      }

      // Group by expense type
      const expenseType = data.expenseType || 'Other';
      if (!byType[expenseType]) {
        byType[expenseType] = { total: 0, count: 0 };
      }
      byType[expenseType].total += amount;
      byType[expenseType].count += 1;
    });

    const averageExpense = expenseCount > 0 ? totalAmount / expenseCount : 0;
    const averageUnitCost = totalQuantity > 0 ? totalAmount / totalQuantity : 0;

    return {
      totalAmount,
      totalQuantity,
      expenseCount,
      averageExpense,
      averageUnitCost,
      byCategory: Object.entries(byCategory).map(([category, data]) => ({
        category,
        total: data.total,
        count: data.count,
        percentage: totalAmount > 0 ? (data.total / totalAmount) * 100 : 0
      })),
      byProduct: Object.values(byProduct).map((data) => ({
        ...data,
        percentage: totalAmount > 0 ? (data.total / totalAmount) * 100 : 0
      })),
      byType: Object.entries(byType).map(([type, data]) => ({
        type,
        total: data.total,
        count: data.count,
        percentage: totalAmount > 0 ? (data.total / totalAmount) * 100 : 0
      }))
    };
  } catch (error) {
    console.error('Error getting inventory stats:', error);
    throw error;
  }
};

// Export as object for compatibility with API pattern
const inventoryService = {
  getAll,
  getById,
  create,
  update,
  delete: remove,
  getStats
};

export default inventoryService;
